import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createRefetchContainer } from 'react-relay';
import classNames from 'classnames';

// components
import { FormattedPrice } from 'dibs-buyer-price';
import ShippingIcon from 'dibs-icons/exports/Shipping';
import { PdpSharedBlurb } from './PdpSharedBlurb/PdpSharedBlurb';
import {
    deliveryTitle,
    freeShippingTitle,
    freeWhiteGloveShippingTitle,
    whiteGloveShippingTitle,
    shipsFrom,
    shipsFromLocation,
} from '../messages/pdpBlurbMessages';
import { PdpSharedShippingInfo } from '../PdpSharedShippingInfo/PdpSharedShippingInfo';
import { PdpSharedShippingInfoLink } from '../PdpSharedShippingInfo/PdpSharedShippingInfoLink';

// helpers
import { getIsFreeShipping } from 'dibs-search-product-tile/exports/freeShippingHelpers';
import { isPriceAboveThreshold, getShippingPriceAmount } from '../helpers/shippingHelpers';
import { getUserSessionCountryCode } from 'dibs-buyer-layout/exports/regionalInfoHelpers';

// styles
import styles from './PdpSharedBlurbs.scss';
export class PdpSharedBlurbShippingComponent extends React.Component {
    constructor(props) {
        super(props);
        this.doRefetch = this.doRefetch.bind(this);
    }

    getTitleCopy(isFavorable, isFreeShipping) {
        const { currency, shipmentQuote } = this.props;

        if (isFreeShipping && isFavorable) {
            return freeWhiteGloveShippingTitle;
        } else if (isFavorable) {
            const convertedAmountList = shipmentQuote?.buyerSubTotalAmount?.convertedAmountList;
            const priceAmount = getShippingPriceAmount({ convertedAmountList, currency });

            // Edge case (FINDING-14585)
            // priceAmount should never be 0 b/c that would mean it's free shipping
            // and we would've shown freeWhiteGloveShippingTitle with (isFreeShipping && isFavorable) check above
            // But in QA/local dev, getIsFreeShipping will be false when you try to get userRegions with "0" as $userZipCode (default when zipCode isn't set in local/session storage)
            // We then try to use ip address as backup but b/c it's private ip address when QA/local we couldn't get any user geoInfo from it
            return priceAmount === 0
                ? freeWhiteGloveShippingTitle
                : whiteGloveShippingTitle(
                      <FormattedPrice currency={currency} value={priceAmount} />
                  );
        } else if (isFreeShipping) {
            return freeShippingTitle;
        } else {
            return deliveryTitle;
        }
    }
    doRefetch(refetchVariables) {
        const variables = { ...refetchVariables, userCountryCode: getUserSessionCountryCode() };
        this.props.relay.refetch(variables);
    }

    render() {
        const {
            item,
            itemId,
            viewer,
            currency,
            userId,
            user,
            shipmentQuote,
            shipment,
            renderTooltip,
        } = this.props;

        const isVerifiedTrade = user?.isVerifiedTrade || false;
        const itemLocation = item?.address?.itemAddress;
        const isGalleryLocation = item?.address?.isGalleryRoomAddress;
        const leadTime = item?.attributes?.leadTime?.[0]?.option?.urlLabel;
        const quoteRequestStatus = viewer?.relevantShipping?.quoteRequestStatus;
        const showQuoteDetails = quoteRequestStatus !== 'requestQuote';
        const userRegions = viewer?.regionalInfo?.[0]?.regionsByZipCode || [];
        const isFreeShipping = getIsFreeShipping({ item, userRegions });
        const isWhiteGlove = !!shipmentQuote?.serviceMethod?.isWhiteGlove;
        const convertedAmountList = shipmentQuote?.buyerSubTotalAmount?.convertedAmountList;
        const isFavorable = isWhiteGlove && !isPriceAboveThreshold({ convertedAmountList });

        const titleCopy = this.getTitleCopy(isFavorable, isFreeShipping);
        const itemShipsFrom =
            isGalleryLocation && isVerifiedTrade ? `${itemLocation}.` : itemLocation;

        if (!viewer || !viewer.regionalInfo) {
            return null;
        }

        return (
            <PdpSharedBlurb IconComponent={ShippingIcon}>
                <span
                    className={classNames(styles.title, {
                        [styles.titleOnSeparateLine]: isFreeShipping || isFavorable,
                    })}
                >
                    {titleCopy}
                </span>
                <PdpSharedShippingInfo
                    currency={currency}
                    shipmentQuote={shipmentQuote}
                    shipment={shipment}
                    leadTime={leadTime}
                    quoteRequestStatus={quoteRequestStatus}
                    renderTooltip={renderTooltip}
                    noLineBreaks
                    hidePrice={isFreeShipping || isFavorable}
                >
                    <PdpSharedShippingInfoLink
                        shipmentQuote={shipmentQuote}
                        currency={currency}
                        userId={userId}
                        item={item}
                        itemId={itemId}
                        user={user}
                        viewer={viewer}
                        showQuoteDetails={showQuoteDetails}
                        quoteRequestStatus={quoteRequestStatus}
                        onSuccessfulQuoteRequest={this.doRefetch}
                    />
                    <div className={styles.shipsFromContainer}>
                        <span className={styles.shipsFrom}>{shipsFrom}</span>
                        <span>{shipsFromLocation(itemShipsFrom)}</span>
                    </div>
                </PdpSharedShippingInfo>
                <div />
            </PdpSharedBlurb>
        );
    }
}

PdpSharedBlurbShippingComponent.propTypes = {
    currency: PropTypes.string,
    userId: PropTypes.string,
    itemId: PropTypes.string,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object,
    shipmentQuote: PropTypes.object,
    shipment: PropTypes.object,
    relay: PropTypes.object.isRequired,
    renderTooltip: PropTypes.bool,
};

export const PdpSharedBlurbShipping = createRefetchContainer(
    PdpSharedBlurbShippingComponent,
    {
        viewer: graphql`
            fragment PdpSharedBlurbShipping_viewer on Viewer {
                ...PdpSharedShippingInfoLink_viewer
                relevantShipping(itemId: $itemId, buyerId: $userId) {
                    quoteRequestStatus
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    regionsByZipCode {
                        displayName
                    }
                }
            }
        `,
        user: graphql`
            fragment PdpSharedBlurbShipping_user on User {
                isVerifiedTrade
                ...PdpSharedShippingInfoLink_user
            }
        `,
        item: graphql`
            fragment PdpSharedBlurbShipping_item on Item {
                ...PdpSharedShippingInfoLink_item
                hasShipmentQuotes
                hasPreQuotedShipping
                vertical
                itemIsOnHold: isOnHold(buyerId: $userId)
                address {
                    itemAddress: displayCityStateCountry(fullCountryName: true)
                    isGalleryRoomAddress
                }
                attributes {
                    leadTime {
                        option {
                            urlLabel
                        }
                    }
                }
                shipmentQuotes {
                    totalAmount {
                        amount
                    }
                    serviceMethod {
                        region {
                            regionName
                        }
                    }
                }
            }
        `,
        shipmentQuote: graphql`
            fragment PdpSharedBlurbShipping_shipmentQuote on ShipmentQuote {
                isCustom
                isNew
                status
                subTotalAmount {
                    amount
                }
                buyerSubTotalAmount {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                serviceMethod {
                    isWhiteGlove
                }
                ...PdpSharedShippingInfoLink_shipmentQuote
                ...PdpSharedShippingInfo_shipmentQuote
            }
        `,
        shipment: graphql`
            fragment PdpSharedBlurbShipping_shipment on Shipment {
                ...PdpSharedShippingInfo_shipment
            }
        `,
    },
    graphql`
        query PdpSharedBlurbShippingRefetchQuery(
            $userId: String!
            $fetchUser: Boolean!
            $itemId: String!
            $userZipCode: String = "0"
            $userCountryCode: String = ""
            $fetchRegionalInfo: Boolean = false
        ) {
            viewer {
                ...PdpSharedBlurbShipping_viewer
                relevantShipping(itemId: $itemId, buyerId: $userId) {
                    quoteRequestStatus
                    shipmentQuote {
                        ...PdpSharedBlurbShipping_shipmentQuote
                    }
                    shipment {
                        ...PdpSharedBlurbShipping_shipment
                    }
                }
            }
        }
    `
);
